import {createAction} from 'Shared/utilities/actionCreator';
import * as A from 'Users/models/User/Agent/AgentSupportUserType';
import {AgentNumber} from 'Users/models/User/Agent/AgentNumber';
import {
  TaxIdentificationNumberWithNoAgentNumbers,
  ValidTaxIdentificationNumber,
} from 'Users/models/User/Agent/TaxIdentificationNumber';

export enum AgentSupportUserTypeActionType {
  ADD_AGENCY_ASSOCIATION = 'agentSupportUserType/ADD_AGENCY_ASSOCIATION',
  REMOVE_AGENCY_ASSOCIATION = 'agentSupportUserType/REMOVE_AGENCY_ASSOCIATION',
  CHANGE_TAX_ID = 'agentSupportUserType/CHANGE_TAX_ID',
  LOAD_TAX_ID = 'agentSupportUserType/LOAD_TAX_ID',
  ADD_AGENT_NUMBER = 'agentSupportUserType/ADD_AGENT_NUMBER',
  REMOVE_AGENT_NUMBER = 'agentSupportUserType/REMOVE_AGENT_NUMBER',
  CHANGE_CAN_VIEW_ACCOUNTS = 'agentSupportUserType/CHANGE_CAN_VIEW_ACCOUNTS',
  CHANGE_CAN_VIEW_POLICIES = 'agentSupportUserType/CHANGE_CAN_VIEW_POLICIES',
  CHANGE_CAN_VIEW_CLAIMS = 'agentSupportUserType/CHANGE_CAN_VIEW_CLAIMS',
  CHANGE_CAN_VIEW_COMMISSIONS_AND_1099S = 'agentSupportUserType/CHANGE_CAN_VIEW_COMMISSIONS_AND_1099S',
  CHANGE_CAN_VIEW_POLICY_SERVICE_REQUEST = 'agentSupportUserType/CHANGE_CAN_VIEW_POLICY_SERVICE_REQUEST',
  CHANGE_CAN_VIEW_CORRESPONDENCE = 'agentSupportUserType/CHANGE_CAN_VIEW_CORRESPONDENCE',
  CHANGE_CAN_VIEW_PENDING_REQUIREMENTS = 'agentSupportUserType/CHANGE_CAN_VIEW_PENDING_REQUIREMENTS',
  LOAD_AGENT_SUPPORT_USER_TYPE = 'agentSupportUserType/LOAD_AGENT_SUPPORT_USER_TYPE',
}

export const addAgencyAssociation = () => createAction(AgentSupportUserTypeActionType.ADD_AGENCY_ASSOCIATION);
export const removeAgencyAssociation = (index: number) =>
  createAction(AgentSupportUserTypeActionType.REMOVE_AGENCY_ASSOCIATION, {index});
export const loadTaxId = (
  index: number,
  taxId: ValidTaxIdentificationNumber | TaxIdentificationNumberWithNoAgentNumbers
) => createAction(AgentSupportUserTypeActionType.LOAD_TAX_ID, {taxId, index});
export const changeTaxId = (index: number, taxId: string) =>
  createAction(AgentSupportUserTypeActionType.CHANGE_TAX_ID, {taxId, index});
export const addAgentNumber = (index: number, agentNumber: AgentNumber) =>
  createAction(AgentSupportUserTypeActionType.ADD_AGENT_NUMBER, {agentNumber, index});
export const removeAgentNumber = (index: number, agentNumber: AgentNumber) =>
  createAction(AgentSupportUserTypeActionType.REMOVE_AGENT_NUMBER, {agentNumber, index});
export const changeCanViewAccounts = (index: number, canViewAccounts: boolean) =>
  createAction(AgentSupportUserTypeActionType.CHANGE_CAN_VIEW_ACCOUNTS, {canViewAccounts, index});
export const changeCanViewPolicies = (index: number, canViewPolicies: boolean) =>
  createAction(AgentSupportUserTypeActionType.CHANGE_CAN_VIEW_POLICIES, {canViewPolicies, index});
export const changeCanViewClaims = (index: number, canViewClaims: boolean) =>
  createAction(AgentSupportUserTypeActionType.CHANGE_CAN_VIEW_CLAIMS, {canViewClaims, index});
export const changeCanViewCommissionsAnd1099s = (index: number, canViewCommissionsAnd1099s: boolean) =>
  createAction(AgentSupportUserTypeActionType.CHANGE_CAN_VIEW_COMMISSIONS_AND_1099S, {
    canViewCommissionsAnd1099s,
    index,
  });
export const changeCanViewPolicyServiceRequest = (index: number, canViewPolicyServiceRequest: boolean) =>
  createAction(AgentSupportUserTypeActionType.CHANGE_CAN_VIEW_POLICY_SERVICE_REQUEST, {
    canViewPolicyServiceRequest,
    index,
  });
export const changeCanViewCorrespondence = (index: number, canViewCorrespondence: boolean) =>
  createAction(AgentSupportUserTypeActionType.CHANGE_CAN_VIEW_CORRESPONDENCE, {canViewCorrespondence, index});
export const changeCanViewPendingUnderwritingRequirements = (
  index: number,
  canViewPendingUnderwritingRequirements: boolean
) =>
  createAction(AgentSupportUserTypeActionType.CHANGE_CAN_VIEW_PENDING_REQUIREMENTS, {
    canViewPendingUnderwritingRequirements,
    index,
  });
export const loadAgentSupportUserType = (agentSupportUserType: A.ValidAgentSupportUserType) =>
  createAction(AgentSupportUserTypeActionType.LOAD_AGENT_SUPPORT_USER_TYPE, {agentSupportUserType});

type AgentSupportUserTypeState = A.AgentSupportUserType;
const DEFAULT_STATE = A.EmptyAgentSupportUserType;

export type AgentSupportUserTypeAction =
  | ReturnType<typeof changeCanViewPendingUnderwritingRequirements>
  | ReturnType<typeof changeCanViewAccounts>
  | ReturnType<typeof changeCanViewPolicies>
  | ReturnType<typeof changeCanViewClaims>
  | ReturnType<typeof changeCanViewCommissionsAnd1099s>
  | ReturnType<typeof changeCanViewPolicyServiceRequest>
  | ReturnType<typeof changeCanViewCorrespondence>
  | ReturnType<typeof addAgencyAssociation>
  | ReturnType<typeof loadTaxId>
  | ReturnType<typeof removeAgencyAssociation>
  | ReturnType<typeof changeTaxId>
  | ReturnType<typeof addAgentNumber>
  | ReturnType<typeof loadAgentSupportUserType>
  | ReturnType<typeof removeAgentNumber>;

export default (
  state: AgentSupportUserTypeState = DEFAULT_STATE,
  action: AgentSupportUserTypeAction
): AgentSupportUserTypeState => {
  switch (action.type) {
    case AgentSupportUserTypeActionType.ADD_AGENCY_ASSOCIATION:
      return A.addAgencyAssociation(state);

    case AgentSupportUserTypeActionType.REMOVE_AGENCY_ASSOCIATION: {
      const {index} = action.payload;

      return A.removeAgencyAssociation(index)(state);
    }
    case AgentSupportUserTypeActionType.LOAD_TAX_ID: {
      const {taxId, index} = action.payload;

      return A.loadTaxIdNumber(index, taxId)(state);
    }
    case AgentSupportUserTypeActionType.ADD_AGENT_NUMBER: {
      const {agentNumber, index} = action.payload;

      return A.addAgentNumberToAgencyAssociation(index, agentNumber)(state);
    }
    case AgentSupportUserTypeActionType.REMOVE_AGENT_NUMBER: {
      const {agentNumber, index} = action.payload;

      return A.removeAgentNumberFromAgencyAssociation(index, agentNumber)(state);
    }
    case AgentSupportUserTypeActionType.CHANGE_TAX_ID: {
      const {taxId, index} = action.payload;

      return A.changeAgencyAssociationTaxId(index, taxId)(state);
    }
    case AgentSupportUserTypeActionType.CHANGE_CAN_VIEW_ACCOUNTS: {
      const {canViewAccounts, index} = action.payload;

      return A.changeAgencyAssociationCanViewAccounts(index, canViewAccounts)(state);
    }
    case AgentSupportUserTypeActionType.CHANGE_CAN_VIEW_POLICIES: {
      const {canViewPolicies, index} = action.payload;

      return A.changeAgencyAssociationCanViewPolicies(index, canViewPolicies)(state);
    }
    case AgentSupportUserTypeActionType.CHANGE_CAN_VIEW_CLAIMS: {
      const {canViewClaims, index} = action.payload;

      return A.changeAgencyAssociationCanViewClaims(index, canViewClaims)(state);
    }
    case AgentSupportUserTypeActionType.CHANGE_CAN_VIEW_COMMISSIONS_AND_1099S: {
      const {canViewCommissionsAnd1099s, index} = action.payload;

      return A.changeAgencyAssociationCanViewCommissionsAnd1099s(index, canViewCommissionsAnd1099s)(state);
    }
    case AgentSupportUserTypeActionType.CHANGE_CAN_VIEW_POLICY_SERVICE_REQUEST: {
      const {canViewPolicyServiceRequest, index} = action.payload;

      return A.changeAgencyAssociationCanViewPolicyServiceRequest(index, canViewPolicyServiceRequest)(state);
    }
    case AgentSupportUserTypeActionType.CHANGE_CAN_VIEW_CORRESPONDENCE: {
      const {canViewCorrespondence, index} = action.payload;

      return A.changeAgencyAssociationCanViewCorrespondence(index, canViewCorrespondence)(state);
    }
    case AgentSupportUserTypeActionType.CHANGE_CAN_VIEW_PENDING_REQUIREMENTS: {
      const {canViewPendingUnderwritingRequirements, index} = action.payload;

      return A.changeAgencyAssociationCanViewPendingUnderwritingRequirements(
        index,
        canViewPendingUnderwritingRequirements
      )(state);
    }
    case AgentSupportUserTypeActionType.LOAD_AGENT_SUPPORT_USER_TYPE: {
      const {agentSupportUserType} = action.payload;

      return agentSupportUserType;
    }
    default:
      return state;
  }
};
